/* eslint-disable react-hooks/exhaustive-deps */
import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { API_ENDPOINTS } from '../../../constants/api';
import useAxios from '../../../hooks/useAxios';
import { Table, Form, Button, Badge } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faBars, faMagnifyingGlass, faCircleXmark, faEye, faCode, faCopy, faCheck } from '@fortawesome/free-solid-svg-icons';
import useSidebar from '../../../hooks/useSidebar';
import PaginationNav from '../../../component/pagination/pagination';
import XLoader from '../../../component/loader/XLoader';
import XModal from '../../../component/modal/XModal';
import { formatAuditTimestamp, debounce, getMaxDateToday } from '../../../utils/helper';
import { useSelector } from 'react-redux';
import { RootState } from '../../../redux/rootReducer';
interface EnterpriseAuditLogEntry {
  id: number;
  org_id: number;
  location_id: number | null;
  location_name?: string | null;
  location_timezone?: string | null;
  performed_by_id: number | null;
  performed_by_name: string | null;
  performed_by_email: string | null;
  performed_by_role: string | null;
  event_type: string;
  action: string;
  entity_type: string | null;
  entity_id: number | null;
  target_user_id: number | null;
  target_user_name: string | null;
  metadata: any;
  ip_address: string | null;
  user_agent: string | null;
  created_at: string;
}

const AUDIT_MODULE_OPTIONS = [
  { value: 'all', label: 'All Modules' },
  { value: 'CAMPAIGNS', label: 'Campaigns' },
  { value: 'JOURNEYS', label: 'Journeys' },
  { value: 'TEMPLATES', label: 'Templates' },
  { value: 'LISTS_SEGMENTS', label: 'Lists & Segments' },
  { value: 'USERS_ACCESS', label: 'Users & Access' },
  { value: 'LOCATIONS_DOMAINS', label: 'Locations & Domains' },
  { value: 'CONTACTS', label: 'Contacts & CSV' },
];

const EnterpriseAuditLog: React.FC = () => {
  const [auditData, setAuditData] = useState<EnterpriseAuditLogEntry[]>([]);
  const { fetchData, isLoading } = useAxios();
  const { onShowSidebar } = useSidebar();
  const { orgLocations, selectedLocationId } = useSelector((state: RootState) => state.user);

  // Selected Location object from active global selection
  const activeSelectedLocation = useMemo(() => {
    return orgLocations?.find((loc: any) => String(loc.id) === String(selectedLocationId));
  }, [orgLocations, selectedLocationId]);

  const activeLocationTimezone = activeSelectedLocation?.location_timezone || 'UTC';

  const [timeFormatMode, setTimeFormatMode] = useState<'location' | 'utc' | 'local'>('location');
  const [totalPages, setTotalPages] = useState<number>(1);
  const [selectedEntry, setSelectedEntry] = useState<EnterpriseAuditLogEntry | null>(null);
  const [showDetailsModal, setShowDetailsModal] = useState<boolean>(false);
  const [copiedCode, setCopiedCode] = useState<boolean>(false);

  // Filters state
  const [searchTerm, setSearchTerm] = useState<string>('');
  const [selectedEventTypeFilter, setSelectedEventTypeFilter] = useState<string>('all');
  const [startDate, setStartDate] = useState<string>('');
  const [endDate, setEndDate] = useState<string>('');
  const [page, setPage] = useState<number>(1);
  const limit = 10;
  const todayDate = useMemo(() => getMaxDateToday(), []);

  useEffect(() => {
    setPage(1);
  }, [selectedLocationId]);

  const fetchLogs = async () => {
    try {
      let query = `${API_ENDPOINTS.ENTERPRISE_AUDIT_LOGS}?page=${page}&limit=${limit}`;

      if (selectedLocationId) {
        query += `&location_id=${selectedLocationId}`;
      }
      if (searchTerm.trim()) {
        query += `&search=${encodeURIComponent(searchTerm.trim())}`;
      }
      if (selectedEventTypeFilter && selectedEventTypeFilter !== 'all') {
        query += `&event_type=${selectedEventTypeFilter}`;
      }
      if (startDate) {
        query += `&start_date=${startDate}`;
      }
      if (endDate) {
        query += `&end_date=${endDate}`;
      }

      const response = await fetchData(query);
      if (response && response.data) {
        setAuditData(response.data.auditData || []);
        const total = response.data.totalCount || 0;
        setTotalPages(Math.ceil(total / limit) || 1);
      }
    } catch (error) {
      console.error('Error fetching enterprise audit logs:', error);
    }
  };

  useEffect(() => {
    fetchLogs();
  }, [page, selectedLocationId, selectedEventTypeFilter, startDate, endDate, searchTerm]);

  // Debounced search
  const debouncedSearch = useCallback(
    debounce((value: string) => {
      setPage(1);
      setSearchTerm(value);
    }, 400),
    []
  );

  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    debouncedSearch(e.target.value);
  };

  const handlePageChange = (pageNumber: number) => {
    setPage(pageNumber);
  };

  const handleClearFilters = () => {
    setSearchTerm('');
    setSelectedEventTypeFilter('all');
    setStartDate('');
    setEndDate('');
    setPage(1);
  };

  const hasActiveFilters =
    Boolean(searchTerm) ||
    selectedEventTypeFilter !== 'all' ||
    Boolean(startDate) ||
    Boolean(endDate);

  const startingIndex = (page - 1) * limit;

  const renderEventBadge = (eventType: string) => {
    let label = eventType ? eventType.replace(/_/g, ' ') : '-';
    let color = '#475569'; // default slate

    switch (eventType) {
      // Green (Success / Active / Verified / Granted)
      case 'ACCESS_GRANTED':
        label = 'Access Granted';
        color = '#16A34A';
        break;
      case 'USER_ACTIVATED':
        label = 'User Activated';
        color = '#16A34A';
        break;
      case 'LOCATION_CREATED':
        label = 'Location Created';
        color = '#16A34A';
        break;
      case 'LOCATION_REACTIVATED':
        label = 'Location Reactivated';
        color = '#16A34A';
        break;
      case 'DOMAIN_VERIFIED':
        label = 'Domain Verified';
        color = '#16A34A';
        break;
      case 'CONTACT_RESUBSCRIBED':
        label = 'Contact Re-subscribed';
        color = '#16A34A';
        break;
      case 'PHONE_NUMBER_PURCHASED':
        label = 'Number Purchased';
        color = '#16A34A';
        break;

      // Blue (Creation / Initiation)
      case 'USER_INVITED':
        label = 'User Invited';
        color = '#2563EB';
        break;
      case 'DOMAIN_ADDED':
        label = 'Domain Added';
        color = '#2563EB';
        break;
      case 'CAMPAIGN_CREATED':
        label = 'Campaign Created';
        color = '#2563EB';
        break;
      case 'JOURNEY_CREATED':
        label = 'Journey Created';
        color = '#2563EB';
        break;
      case 'TEMPLATE_CREATED':
        label = 'Template Created';
        color = '#2563EB';
        break;
      case 'LIST_CREATED':
        label = 'List Created';
        color = '#2563EB';
        break;
      case 'SEGMENT_CREATED':
        label = 'Segment Created';
        color = '#2563EB';
        break;

      // Amber / Orange (Modifications / Updates)
      case 'USER_UPDATED':
        label = 'User Updated';
        color = '#D97706';
        break;
      case 'LOCATION_UPDATED':
        label = 'Location Updated';
        color = '#D97706';
        break;
      case 'CAMPAIGN_UPDATED':
        label = 'Campaign Updated';
        color = '#D97706';
        break;
      case 'JOURNEY_UPDATED':
        label = 'Journey Updated';
        color = '#D97706';
        break;
      case 'TEMPLATE_UPDATED':
        label = 'Template Updated';
        color = '#D97706';
        break;
      case 'LIST_UPDATED':
        label = 'List Updated';
        color = '#D97706';
        break;
      case 'SEGMENT_UPDATED':
        label = 'Segment Updated';
        color = '#D97706';
        break;
      case 'CONTACT_UNSUBSCRIBED':
        label = 'Contact Unsubscribed';
        color = '#D97706';
        break;

      // Red (Deletions / Cancellations / Revocations)
      case 'ACCESS_REVOKED':
        label = 'Access Revoked';
        color = '#DC2626';
        break;
      case 'LOCATION_DEACTIVATED':
        label = 'Location Deactivated';
        color = '#DC2626';
        break;
      case 'CAMPAIGN_CANCELLED':
        label = 'Campaign Cancelled';
        color = '#DC2626';
        break;
      case 'TEMPLATE_DELETED':
        label = 'Template Deleted';
        color = '#DC2626';
        break;
      case 'LIST_DELETED':
        label = 'List Deleted';
        color = '#DC2626';
        break;
      case 'SEGMENT_DELETED':
        label = 'Segment Deleted';
        color = '#DC2626';
        break;
      case 'CONTACT_DELETED':
        label = 'Contact Deleted';
        color = '#DC2626';
        break;

      // Cyan / Info (Transitions / Bulk)
      case 'ACCESS_UPDATED':
        label = 'Access Updated';
        color = '#0284C7';
        break;
      case 'USER_INVITE_RESENT':
        label = 'Invite Resent';
        color = '#0284C7';
        break;
      case 'CAMPAIGN_SCHEDULED':
        label = 'Campaign Scheduled';
        color = '#0284C7';
        break;
      case 'JOURNEY_STATUS_UPDATED':
        label = 'Journey Status Updated';
        color = '#0284C7';
        break;
      case 'CONTACTS_BULK_UPLOADED':
        label = 'Contacts Bulk Uploaded';
        color = '#0284C7';
        break;

      // Gray
      case 'USER_DEACTIVATED':
        label = 'User Deleted';
        color = '#64748B';
        break;

      default:
        label = eventType ? eventType.replace(/_/g, ' ') : '-';
        color = '#475569';
    }

    return (
      <span
        className="fw-semibold text-nowrap"
        style={{
          color: color,
          fontSize: '13px',
        }}
      >
        {label}
      </span>
    );
  };

  const renderRoleBadge = (role: string | number | null | undefined) => {
    if (!role) return null;
    const str = String(role).trim().toLowerCase();

    // Not include super admin or its users
    if (str.includes('super admin') || str === '1' || str === '4') {
      return null;
    }

    // Account Owner (org owner)
    if (str.includes('owner') || str.includes('organisation') || str === 'org' || str === '3') {
      return (
        <span
          className="badge"
          style={{
            backgroundColor: '#4F46E5', // Royal Indigo
            color: '#FFFFFF',
            fontSize: '10px',
            fontWeight: 500,
            padding: '3px 8px',
            borderRadius: '4px',
            letterSpacing: '0.2px',
          }}
        >
          Account Owner
        </span>
      );
    }

    // Account Admin
    if (str.includes('account admin') || str === '6') {
      return (
        <span
          className="badge"
          style={{
            backgroundColor: '#7BAFD4', // Blue
            color: '#FFFFFF',
            fontSize: '10px',
            fontWeight: 500,
            padding: '3px 8px',
            borderRadius: '4px',
            letterSpacing: '0.2px',
          }}
        >
          Account Admin
        </span>
      );
    }

    // Location Admin
    if (str.includes('location admin') || str === '7') {
      return (
        <span
          className="badge"
          style={{
            backgroundColor: '#0D9488', // Teal
            color: '#FFFFFF',
            fontSize: '10px',
            fontWeight: 500,
            padding: '3px 8px',
            borderRadius: '4px',
            letterSpacing: '0.2px',
          }}
        >
          Location Admin
        </span>
      );
    }

    // Location Staff
    if (str.includes('location staff') || str.includes('org staff') || str === 'staff' || str === 'user' || str === '8' || str === '2') {
      return (
        <span
          className="badge"
          style={{
            backgroundColor: '#64748B', // Slate
            color: '#FFFFFF',
            fontSize: '10px',
            fontWeight: 500,
            padding: '3px 8px',
            borderRadius: '4px',
            letterSpacing: '0.2px',
          }}
        >
          Location Staff
        </span>
      );
    }

    // Read Only
    if (str.includes('read only') || str.includes('readonly') || str === '5') {
      return (
        <span
          className="badge"
          style={{
            backgroundColor: '#D97706', // Warm Amber
            color: '#FFFFFF',
            fontSize: '10px',
            fontWeight: 500,
            padding: '3px 8px',
            borderRadius: '4px',
            letterSpacing: '0.2px',
          }}
        >
          Read Only
        </span>
      );
    }

    return null;
  };

  const getDisplayTime = (entry: EnterpriseAuditLogEntry) => {
    const rawTimestamp = entry.created_at;
    if (!rawTimestamp) return '-';

    if (timeFormatMode === 'utc') {
      return formatAuditTimestamp(rawTimestamp, 'UTC');
    }
    if (timeFormatMode === 'location') {
      const rowTz = entry.location_timezone || activeLocationTimezone;
      return formatAuditTimestamp(rawTimestamp, rowTz);
    }
    // 'local' browser device time
    return formatAuditTimestamp(rawTimestamp);
  };
  const formatIpAddress = (ip: string | null | undefined) => {
    if (!ip) return '-';
    let cleaned = ip.trim();
    if (cleaned.startsWith('::ffff:')) {
      cleaned = cleaned.substring(7);
    }
    if (cleaned === '::1' || cleaned === '127.0.0.1') {
      return '127.0.0.1 (Localhost)';
    }
    return cleaned;
  };

  const parseMetadata = (metadata: any) => {
    if (!metadata) return null;
    if (typeof metadata === 'object') return metadata;
    try {
      return JSON.parse(metadata);
    } catch (e) {
      return metadata;
    }
  };

  const formatJsonAsCode = (data: any) => {
    if (!data) return '<span style="color: #8b949e">// No change payload metadata recorded</span>';
    let parsed = data;
    if (typeof data === 'string') {
      try {
        parsed = JSON.parse(data);
      } catch (e) {
        parsed = data;
      }
    }
    const jsonString = typeof parsed === 'object' ? JSON.stringify(parsed, null, 2) : String(parsed);
    const escaped = jsonString
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;');

    return escaped.replace(
      /("(\\u[a-zA-Z0-9]{4}|\\[^u]|[^\\"])*"(\s*:)?|\b(true|false|null)\b|-?\d+(?:\.\d*)?(?:[eE][+\-]?\d+)?)/g,
      (match) => {
        let color = '#79c0ff'; // string values
        if (/^"/.test(match)) {
          if (/:$/.test(match)) {
            color = '#7ee787'; // key
          } else {
            color = '#a5d6ff'; // string value
          }
        } else if (/true|false/.test(match)) {
          color = '#ff7b72'; // boolean
        } else if (/null/.test(match)) {
          color = '#8b949e'; // null
        } else {
          color = '#d2a8ff'; // number
        }
        return `<span style="color: ${color}">${match}</span>`;
      }
    );
  };

  const handleCopyCode = (metadata: any) => {
    if (!metadata) return;
    try {
      const text = typeof metadata === 'string'
        ? (() => {
            try {
              return JSON.stringify(JSON.parse(metadata), null, 2);
            } catch (e) {
              return metadata;
            }
          })()
        : JSON.stringify(metadata, null, 2);

      navigator.clipboard.writeText(text);
      setCopiedCode(true);
      setTimeout(() => setCopiedCode(false), 2000);
    } catch (e) {
      console.error('Failed to copy payload:', e);
    }
  };

  return (
    <div className="enterprise-audit-log-container">
      {/* Header with Title and Timezone Mode Toggle */}
      <div className="main-title d-flex align-items-center justify-content-between flex-wrap gap-2 mb-3">
        <h2 className="d-flex align-items-center mb-0">
          <span className="d-lg-none cursor-pointer me-2" onClick={() => onShowSidebar()}>
            <FontAwesomeIcon icon={faBars} />
          </span>
          Audit Logs
        </h2>

        {/* Display Timezone Toggle */}
        <div className="d-flex align-items-center gap-2 flex-wrap">
          <span className="text-muted small fw-medium">Display Time:</span>
          <div className="btn-group btn-group-sm" role="group" aria-label="Audit time format toggle">
            <button
              type="button"
              className="btn"
              style={{
                fontSize: '12px',
                padding: '4px 12px',
                fontWeight: 500,
                backgroundColor: timeFormatMode === 'location' ? '#13294B' : '#fff',
                borderColor: '#13294B',
                color: timeFormatMode === 'location' ? '#fff' : '#13294B',
              }}
              onClick={() => setTimeFormatMode('location')}
              title={`Selected Location Timezone: ${activeLocationTimezone}`}
            >
              Location Time ({activeLocationTimezone?.split('/').pop()?.replace(/_/g, ' ') || 'Tz'})
            </button>
            <button
              type="button"
              className="btn"
              style={{
                fontSize: '12px',
                padding: '4px 12px',
                fontWeight: 500,
                backgroundColor: timeFormatMode === 'utc' ? '#13294B' : '#fff',
                borderColor: '#13294B',
                color: timeFormatMode === 'utc' ? '#fff' : '#13294B',
              }}
              onClick={() => setTimeFormatMode('utc')}
            >
              UTC
            </button>
            <button
              type="button"
              className="btn"
              style={{
                fontSize: '12px',
                padding: '4px 12px',
                fontWeight: 500,
                backgroundColor: timeFormatMode === 'local' ? '#13294B' : '#fff',
                borderColor: '#13294B',
                color: timeFormatMode === 'local' ? '#fff' : '#13294B',
              }}
              onClick={() => setTimeFormatMode('local')}
            >
              Local Time
            </button>
          </div>
        </div>
      </div>

      {/* Filter Bar matching project existing pages */}
      <div className="filter-bar d-flex align-items-center justify-content-between w-100 flex-wrap gap-3">
        <div className="position-relative search-bar">
          <Form.Control
            type="search"
            placeholder="Search by name, email, action..."
            aria-label="Search by name, email, action"
            defaultValue={searchTerm}
            onChange={handleSearchChange}
          />
          <FontAwesomeIcon icon={faMagnifyingGlass} />
        </div>

        <div className="select-box d-flex align-items-center flex-wrap gap-3 justify-content-end">
          <Form.Group className="d-flex align-items-center mb-0" controlId="filterByModule">
            <Form.Label>Filter By</Form.Label>
            <Form.Select
              aria-label="Filter by Module"
              value={selectedEventTypeFilter}
              onChange={(e) => {
                setPage(1);
                setSelectedEventTypeFilter(e.target.value);
              }}
            >
              {AUDIT_MODULE_OPTIONS.map((opt) => (
                <option key={opt.value} value={opt.value}>
                  {opt.label}
                </option>
              ))}
            </Form.Select>
          </Form.Group>

          <div className="d-flex align-items-center gap-2">
            <Form.Control
              type="date"
              value={startDate}
              max={endDate || todayDate}
              onChange={(e) => {
                const val = e.target.value;
                if (val && val > (endDate || todayDate)) return;
                setPage(1);
                setStartDate(val);
              }}
              style={{
                width: '160px',
                padding: '12px 14px',
                background: '#F9F9F9',
                border: 'unset',
                borderRadius: '6px',
                color: '#000',
              }}
              title="From Date"
            />
            <Form.Control
              type="date"
              value={endDate}
              min={startDate || undefined}
              max={todayDate}
              onChange={(e) => {
                const val = e.target.value;
                if (val && val > todayDate) return;
                setPage(1);
                setEndDate(val);
              }}
              style={{
                width: '160px',
                padding: '12px 14px',
                background: '#F9F9F9',
                border: 'unset',
                borderRadius: '6px',
                color: '#000',
              }}
              title="To Date"
            />
          </div>

          {hasActiveFilters && (
            <Button
              variant="link"
              size="sm"
              className="text-danger p-0 text-decoration-none fw-medium"
              onClick={handleClearFilters}
            >
              <FontAwesomeIcon icon={faCircleXmark} className="me-1" /> Reset
            </Button>
          )}
        </div>
      </div>

      {/* Audit Data Table */}
      <div className="table-listing all-contacts users-list position-relative">
        <Table responsive hover className="align-middle mb-0">
          <thead>
            <tr>
              <th style={{ width: '4%' }}>#</th>
              <th style={{ width: '18%' }}>
                Timestamp ({timeFormatMode === 'location' ? 'Location' : timeFormatMode === 'utc' ? 'UTC' : 'Local'})
              </th>
              <th style={{ width: '16%' }}>Performed By</th>
              <th style={{ width: '14%' }}>Action Type</th>
              <th style={{ width: '26%' }}>Description</th>
              <th style={{ width: '14%' }}>Location</th>
              <th style={{ width: '8%' }} className="text-center">Details</th>
            </tr>
          </thead>
          <tbody>
            {!isLoading && auditData?.length === 0 && (
              <tr>
                <td colSpan={7}>
                  <p className="text-center no-records py-4 my-2 text-muted">
                    No audit records found for this location or filters.
                  </p>
                </td>
              </tr>
            )}

            {!isLoading &&
              auditData &&
              auditData.map((entry: EnterpriseAuditLogEntry, i: number) => {
                const displayTime = getDisplayTime(entry);

                return (
                  <tr key={entry.id || i}>
                    <td>{startingIndex + i + 1}</td>
                    <td>
                      <span className="fw-medium text-nowrap" style={{ fontSize: '13px' }}>
                        {displayTime}
                      </span>
                      {entry.location_timezone && timeFormatMode === 'location' && (
                        <div className="small text-muted" style={{ fontSize: '11px' }}>
                          {entry.location_timezone}
                        </div>
                      )}
                    </td>
                    <td>
                      <div className="fw-medium text-dark" style={{ fontSize: '13px' }}>
                        {entry.performed_by_name || 'System / Admin'}
                      </div>
                      {entry.performed_by_email && (
                        <div className="text-muted small" style={{ fontSize: '11px' }}>
                          {entry.performed_by_email}
                        </div>
                      )}
                      {entry.performed_by_role && (
                        <div className="mt-1">
                          {renderRoleBadge(entry.performed_by_role)}
                        </div>
                      )}
                    </td>
                    <td>{renderEventBadge(entry.event_type)}</td>
                    <td>
                      <div className="text-dark" style={{ fontSize: '13px' }}>
                        {entry.action || '-'}
                      </div>
                      {entry.target_user_name && (
                        <div className="small text-muted mt-1" style={{ fontSize: '11px' }}>
                          Target: <span className="fw-medium text-dark">{entry.target_user_name}</span>
                        </div>
                      )}
                    </td>
                    <td>
                      {entry.location_name ? (
                        <Badge bg="light" text="dark" className="border">
                          {entry.location_name}
                        </Badge>
                      ) : (
                        <Badge bg="secondary" className="bg-opacity-10 text-dark border">
                          All Locations
                        </Badge>
                      )}
                    </td>
                    <td className="text-center">
                      <FontAwesomeIcon
                        icon={faEye}
                        className="cursor-pointer"
                        title="View Details"
                        onClick={() => {
                          setSelectedEntry(entry);
                          setShowDetailsModal(true);
                        }}
                      />
                    </td>
                  </tr>
                );
              })}
          </tbody>
        </Table>
        {isLoading && <XLoader />}
      </div>

      {/* Pagination Nav */}
      {!isLoading && auditData?.length > 0 && (
        <PaginationNav
          totalPages={totalPages}
          currentPage={page}
          handlePageChange={handlePageChange}
        />
      )}

      {/* Details Modal */}
      {/* Details Modal using project standard XModal */}
      <XModal
        show={showDetailsModal}
        handleClose={() => setShowDetailsModal(false)}
        size="lg"
        cancelText="Close"
        title={
          <div className="d-flex align-items-center gap-2">
            <span>Audit Entry Details</span>
            {selectedEntry && (
              <Badge bg="light" text="dark" className="border fw-normal align-middle" style={{ fontSize: '13px' }}>
                #{selectedEntry.id}
              </Badge>
            )}
          </div>
        }
        body={
          selectedEntry ? (
            <div className="audit-entry-details">
              {/* Event Overview Banner */}
              <div
                className="p-3 mb-3"
                style={{
                  background: '#F8FAFC',
                  borderRadius: '8px',
                  border: '1px solid #E2E8F0',
                }}
              >
                <div className="d-flex align-items-center justify-content-between flex-wrap gap-2 mb-2">
                  <div className="d-flex align-items-center gap-2 flex-wrap">
                    {renderEventBadge(selectedEntry.event_type)}
                    <span className="fw-semibold text-dark" style={{ fontSize: '14px' }}>
                      {selectedEntry.action}
                    </span>
                  </div>
                  <span className="text-muted small fw-medium">
                    {getDisplayTime(selectedEntry)}
                  </span>
                </div>
                {selectedEntry.target_user_name && (
                  <div className="small text-muted mt-1">
                    Target User: <span className="fw-medium text-dark">{selectedEntry.target_user_name}</span>
                    {selectedEntry.target_user_id ? ` (ID: #${selectedEntry.target_user_id})` : ''}
                  </div>
                )}
              </div>

              {/* Context Summary Cards */}
              <div className="row g-2 mb-3">
                {/* Performed By Card */}
                <div className="col-12 col-md-6">
                  <div
                    className="p-3 h-100"
                    style={{
                      background: '#FFFFFF',
                      borderRadius: '8px',
                      border: '1px solid #E2E8F0',
                    }}
                  >
                    <div className="text-muted small fw-semibold" style={{ fontSize: '11px', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                      Performed By
                    </div>
                    <div className="fw-semibold text-dark mt-1" style={{ fontSize: '13px' }}>
                      {selectedEntry.performed_by_name || 'System / Admin'}
                    </div>
                    {selectedEntry.performed_by_email && (
                      <div className="text-muted small" style={{ fontSize: '12px' }}>
                        {selectedEntry.performed_by_email}
                      </div>
                    )}
                    {selectedEntry.performed_by_role && (
                      <div className="mt-2">
                        {renderRoleBadge(selectedEntry.performed_by_role)}
                      </div>
                    )}
                  </div>
                </div>

                {/* Scope & Network Card */}
                <div className="col-12 col-md-6">
                  <div
                    className="p-3 h-100"
                    style={{
                      background: '#FFFFFF',
                      borderRadius: '8px',
                      border: '1px solid #E2E8F0',
                    }}
                  >
                    <div className="text-muted small fw-semibold" style={{ fontSize: '11px', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                      Scope & Network
                    </div>
                    <div className="mt-1 small">
                      <span className="text-muted">Location: </span>
                      <span className="fw-semibold text-dark">
                        {selectedEntry.location_name || 'Org-wide'}
                      </span>
                      {selectedEntry.location_timezone && (
                        <span className="text-muted ms-1">({selectedEntry.location_timezone})</span>
                      )}
                    </div>
                    {selectedEntry.ip_address && (
                      <div className="small text-muted mt-1" style={{ fontSize: '12px' }}>
                        <span>IP Address: </span>
                        <code className="text-dark bg-light px-1 py-0.5 rounded border">
                          {formatIpAddress(selectedEntry.ip_address)}
                        </code>
                      </div>
                    )}
                    {selectedEntry.user_agent && (
                      <div className="small text-muted mt-1" style={{ fontSize: '12px' }}>
                        <span>Browser / Device: </span>
                        <code
                          className="text-dark bg-light px-1 py-0.5 rounded border text-break"
                          title={selectedEntry.user_agent}
                        >
                          {(() => {
                            const parts = selectedEntry.user_agent.trim().split(' ');
                            const browser =
                              parts.length >= 2
                                ? parts[parts.length - 2] + parts[parts.length - 1]
                                : selectedEntry.user_agent;

                            const lower = selectedEntry.user_agent.toLowerCase();
                            let os = '';
                            if (lower.includes('macintosh') || lower.includes('mac os')) os = ' (macOS)';
                            else if (lower.includes('windows')) os = ' (Windows)';
                            else if (lower.includes('iphone')) os = ' (iPhone)';
                            else if (lower.includes('ipad')) os = ' (iPad)';
                            else if (lower.includes('android')) os = ' (Android)';
                            else if (lower.includes('linux')) os = ' (Linux)';

                            return `${browser}${os}`;
                          })()}
                        </code>
                      </div>
                    )}
                  </div>
                </div>
              </div>

              {/* Code Section: Change Payload / Metadata */}
              <div>
                <div
                  className="d-flex align-items-center justify-content-between px-3"
                  style={{
                    height: '42px',
                    backgroundColor: '#161b22',
                    borderTopLeftRadius: '8px',
                    borderTopRightRadius: '8px',
                    border: '1px solid #30363d',
                    borderBottom: 'unset',
                  }}
                >
                  <div className="d-flex align-items-center gap-2" style={{ lineHeight: 1 }}>
                    <FontAwesomeIcon icon={faCode} style={{ color: '#58a6ff', fontSize: '14px' }} />
                    <span style={{ color: '#c9d1d9', fontSize: '13px', fontWeight: 600, lineHeight: 1 }}>
                      Change Payload / Metadata (JSON)
                    </span>
                  </div>

                  {selectedEntry.metadata && (
                    <button
                      type="button"
                      className="btn d-inline-flex align-items-center justify-content-center gap-1 shadow-none"
                      style={{
                        height: '26px',
                        padding: '0 10px',
                        margin: 0,
                        backgroundColor: copiedCode ? '#238636' : '#21262d',
                        borderColor: copiedCode ? '#238636' : '#30363d',
                        color: '#c9d1d9',
                        fontSize: '11px',
                        fontWeight: 500,
                        lineHeight: 1,
                        borderRadius: '4px',
                      }}
                      onClick={() => handleCopyCode(selectedEntry.metadata)}
                      title="Copy payload JSON"
                    >
                      <FontAwesomeIcon icon={copiedCode ? faCheck : faCopy} style={{ fontSize: '11px' }} />
                      <span style={{ lineHeight: 1 }}>{copiedCode ? 'Copied!' : 'Copy'}</span>
                    </button>
                  )}
                </div>

                <div
                  style={{
                    backgroundColor: '#0d1117',
                    border: '1px solid #30363d',
                    borderBottomLeftRadius: '8px',
                    borderBottomRightRadius: '8px',
                    overflow: 'hidden',
                  }}
                >
                  <pre
                    style={{
                      margin: 0,
                      padding: '16px',
                      backgroundColor: '#0d1117',
                      color: '#c9d1d9',
                      fontFamily: "'SFMono-Regular', Consolas, 'Liberation Mono', Menlo, monospace",
                      fontSize: '12px',
                      lineHeight: '1.6',
                      maxHeight: '300px',
                      overflowY: 'auto',
                      overflowX: 'auto',
                    }}
                    dangerouslySetInnerHTML={{
                      __html: formatJsonAsCode(selectedEntry.metadata),
                    }}
                  />
                </div>
              </div>
            </div>
          ) : null
        }
      />
    </div>
  );
};

export default EnterpriseAuditLog;
